import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "react-router-dom";
import { 
  Building2, 
  Users, 
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  FileText,
  Calendar,
  ArrowLeft
} from "lucide-react";

const ClientList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Sample client data
  const [clients] = useState([
    {
      id: "CL-001",
      name: "Ahmed Al Mansouri",
      company: "ABC Trading LLC",
      email: "ahmed@abctrading.ae",
      phone: "+971 50 123 4567",
      licenseType: "Commercial License",
      licenseStatus: "Active",
      registrationDate: "2024-01-15",
      renewalDate: "2025-01-15",
      totalInvoices: 5,
      totalAmount: 45750.00,
      lastActivity: "2024-12-18"
    },
    {
      id: "CL-002",
      name: "Sarah Johnson",
      company: "XYZ Consultancy",
      email: "sarah@xyzconsult.com",
      phone: "+971 55 987 6543",
      licenseType: "Professional License",
      licenseStatus: "Renewal Due",
      registrationDate: "2024-03-22",
      renewalDate: "2025-03-22",
      totalInvoices: 3,
      totalAmount: 28400.00,
      lastActivity: "2024-12-15"
    },
    {
      id: "CL-003",
      name: "Mohammed Hassan",
      company: "Global Enterprises",
      email: "mohammed@globalent.ae",
      phone: "+971 52 456 7890",
      licenseType: "Industrial License",
      licenseStatus: "Active",
      registrationDate: "2023-11-10",
      renewalDate: "2024-11-10",
      totalInvoices: 8,
      totalAmount: 67200.00,
      lastActivity: "2024-12-20"
    },
    {
      id: "CL-004",
      name: "Lisa Chen",
      company: "Tech Solutions FZ",
      email: "lisa@techsol.ae",
      phone: "+971 56 234 5678",
      licenseType: "Free Zone License",
      licenseStatus: "Expired",
      registrationDate: "2023-08-05",
      renewalDate: "2024-08-05",
      totalInvoices: 4,
      totalAmount: 32100.00,
      lastActivity: "2024-12-10"
    },
    {
      id: "CL-005",
      name: "Omar Al Zaabi",
      company: "Emirates Construction",
      email: "omar@emiratesconst.ae",
      phone: "+971 50 876 5432",
      licenseType: "Commercial License",
      licenseStatus: "Active",
      registrationDate: "2024-06-12",
      renewalDate: "2025-06-12",
      totalInvoices: 2,
      totalAmount: 18900.00,
      lastActivity: "2024-12-19"
    }
  ]);

  const getStatusBadge = (status: string) => {
    const variants = {
      "Active": "default",
      "Renewal Due": "secondary",
      "Expired": "destructive",
      "Pending": "outline"
    };
    return <Badge variant={variants[status as keyof typeof variants] as any}>{status}</Badge>;
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === "all" || client.licenseStatus.toLowerCase().includes(filterStatus.toLowerCase());
    
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.licenseStatus === "Active").length,
    renewalDue: clients.filter(c => c.licenseStatus === "Renewal Due").length,
    expired: clients.filter(c => c.licenseStatus === "Expired").length
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-2xl font-bold text-primary">ConsultPro</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">Home</Link>
              <Link to="/services" className="text-foreground hover:text-primary transition-colors">Services</Link>
              <Link to="/crm" className="text-foreground hover:text-primary transition-colors">CRM</Link>
              <Button variant="outline" asChild>
                <Link to="/crm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to CRM
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-8 px-4 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <Badge className="mb-4" variant="secondary">Client Management</Badge>
              <h1 className="text-4xl font-bold mb-2">Client Database</h1>
              <p className="text-xl text-muted-foreground">
                Manage your client information, track license status, and monitor business relationships.
              </p>
            </div>
            <Button asChild>
              <Link to="/client-form">
                <Plus className="h-4 w-4 mr-2" />
                Add New Client
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="py-8 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <h3 className="text-3xl font-bold text-blue-600">{stats.total}</h3>
                <p className="text-sm text-muted-foreground">Total Clients</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Building2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <h3 className="text-3xl font-bold text-green-600">{stats.active}</h3>
                <p className="text-sm text-muted-foreground">Active Licenses</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Calendar className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                <h3 className="text-3xl font-bold text-orange-600">{stats.renewalDue}</h3>
                <p className="text-sm text-muted-foreground">Renewal Due</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <FileText className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <h3 className="text-3xl font-bold text-red-600">{stats.expired}</h3>
                <p className="text-sm text-muted-foreground">Expired Licenses</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Client Management */}
      <section className="py-8 px-4">
        <div className="container mx-auto">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Client Management</CardTitle>
                  <CardDescription>
                    View and manage all your clients and their license information
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search clients..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  <select 
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="renewal">Renewal Due</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>License Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Renewal Date</TableHead>
                    <TableHead>Total Business</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div>
                          <div className="font-medium">{client.name}</div>
                          <div className="text-sm text-muted-foreground">{client.company}</div>
                          <div className="text-xs text-muted-foreground">{client.id}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center text-sm">
                            <Mail className="h-3 w-3 mr-1" />
                            {client.email}
                          </div>
                          <div className="flex items-center text-sm">
                            <Phone className="h-3 w-3 mr-1" />
                            {client.phone}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{client.licenseType}</Badge>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(client.licenseStatus)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {client.renewalDate}
                          {client.licenseStatus === "Renewal Due" && (
                            <div className="text-xs text-orange-600 font-medium">Due Soon!</div>
                          )}
                          {client.licenseStatus === "Expired" && (
                            <div className="text-xs text-red-600 font-medium">Overdue</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">AED {client.totalAmount.toFixed(2)}</div>
                          <div className="text-xs text-muted-foreground">{client.totalInvoices} invoices</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {client.lastActivity}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" title="View Details">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" title="Edit Client">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" title="Create Invoice" asChild>
                            <Link to="/invoices">
                              <FileText className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {filteredClients.length === 0 && (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No clients found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchTerm || filterStatus !== "all" 
                      ? "Try adjusting your search or filter criteria." 
                      : "Get started by adding your first client."}
                  </p>
                  <Button asChild>
                    <Link to="/client-form">
                      <Plus className="h-4 w-4 mr-2" />
                      Add New Client
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-8 px-4 bg-gray-50">
        <div className="container mx-auto">
          <h2 className="text-2xl font-bold mb-6">Quick Actions</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <Link to="/client-form">
                <CardHeader className="text-center">
                  <Plus className="h-12 w-12 text-primary mx-auto mb-4" />
                  <CardTitle>Add New Client</CardTitle>
                  <CardDescription>
                    Register a new client with complete details and license information
                  </CardDescription>
                </CardHeader>
              </Link>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <Link to="/quotation">
                <CardHeader className="text-center">
                  <FileText className="h-12 w-12 text-primary mx-auto mb-4" />
                  <CardTitle>Create Quotation</CardTitle>
                  <CardDescription>
                    Generate professional quotations for existing or new clients
                  </CardDescription>
                </CardHeader>
              </Link>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <Link to="/invoices">
                <CardHeader className="text-center">
                  <Building2 className="h-12 w-12 text-primary mx-auto mb-4" />
                  <CardTitle>Generate Invoice</CardTitle>
                  <CardDescription>
                    Create and send professional invoices to your clients
                  </CardDescription>
                </CardHeader>
              </Link>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientList;